<template>
  <v-container v-if="$can('browse', 'YachtEngine')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-12"
    >
      <v-col>
        <div class="headline">
          Yacht Engines
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'YachtEngine')"
          color="primary"
          class="ma-0"
          @click="openYachtEnginesEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.manufacturer"
          solo
          label="Manufacturer"
          hide-details
          clearable
          class="mb-2"
          autocomplete="no"
        />
        <v-text-field
          v-model="filtersDirty.model"
          solo
          label="Model"
          hide-details
          clearable
          class="mb-2"
          autocomplete="no"
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="yachtEngines.meta.from"
          :to="yachtEngines.meta.to"
          :total="yachtEngines.meta.total"
          :last-page="yachtEngines.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="yachtEngines.data"
          :loading="yachtEngines.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="yachtEngines.meta.total"
          hide-default-footer
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <yacht-engine-row
              :yacht-engine="item"
              @click.native="openYachtEnginesEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no yacht engines at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  v-if="$can('create', 'YachtEngine')"
                  color="primary"
                  class="ma-0"
                  small
                  @click="openYachtEnginesEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="yachtEngines.meta.last_page"
            :page.sync="pagination.page"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-engine-edit-dialog
      :visible.sync="yachtEngineEditDialog"
      :yacht-engine="yachtEngineDirty"
      @yachtEngine:created="yachtEngineCreated"
      @yachtEngine:updated="yachtEngineUpdated"
      @yachtEngine:deleted="yachtEngineDeleted"
    />
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import YachtEngine from '@/models/YachtEngine'
  import YachtEngineRow from '@/components/settings/yachtEngines/YachtEngineRow'
  import YachtEngineEditDialog from '@/components/settings/yachtEngines/YachtEngineEditDialog'
  import TableFooter from '@/components/helpers/TableFooter'

  export default {
    name: 'YachtEnginesIndex',
    components: {
      YachtEngineRow,
      YachtEngineEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        yachtEngines: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Manufacturer', value: 'manufacturer', sortable: true },
          { text: 'Model', value: 'model', sortable: false },
          { text: 'Power', value: 'power', sortable: false }
        ],
        yachtEngineDirty: undefined,
        yachtEngineEditDialog: false,
        filtersClean: YachtEngine.emptyFilters,
        filtersDirty: cloneDeep(YachtEngine.emptyFilters)
      }
    },
    created() {
      this.fetchYachtEngines()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchYachtEngines()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchYachtEngines() {
        this.yachtEngines.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await YachtEngine.api.fetch(queryParams)

          this.yachtEngines.data = result.data
          this.yachtEngines.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtEngines.loading = false
        }
      },
      openYachtEnginesEditDialog(yachtEngine = undefined) {
        this.yachtEngineDirty = cloneDeep(yachtEngine)
        this.yachtEngineEditDialog = true
      },
      clearFilters() {
        this.filters = cloneDeep(YachtEngine.emptyFilters)
      },
      yachtEngineCreated(yachtEngine) {
        this.yachtEngines.data.unshift(yachtEngine)
      },
      yachtEngineUpdated(yachtEngine) {
        const index = this.yachtEngines.data.findIndex(ye => ye.id === yachtEngine.id)

        this.yachtEngines.data.splice(index, 1, yachtEngine)
      },
      yachtEngineDeleted(yachtEngineId) {
        const index = this.yachtEngines.data.findIndex(ye => ye.id === yachtEngineId)

        this.yachtEngines.data.splice(index, 1)
      }
    }
  }
</script>
